import { motion } from "motion/react";
import { downloadStrengthTestPDF } from "../utils/strengthPdfGenerator";
import { useState } from "react";

interface StrengthTestReportPreviewProps {
  batchId: string;
  reportId: string;
  testDate: string;
  testAge: number; // 7, 14, or 28 days
  strength: number;
  cubeWeight: number;
  cubeSize: string;
  load: number;
  grade: string;
  location: string;
  customerName?: string;
  projectName?: string;
  technicianName?: string;
  testingMachine?: string;
  onClose: () => void;
}

export default function StrengthTestReportPreview({
  batchId,
  reportId,
  testDate,
  testAge,
  strength,
  cubeWeight,
  cubeSize,
  load,
  grade,
  location,
  customerName = "ABC Construction Ltd.",
  projectName = "Metro Tower Phase 2",
  technicianName = "Rajesh Kumar",
  testingMachine = "CTM-3000 Compression Testing Machine",
  onClose,
}: StrengthTestReportPreviewProps) {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // Calculate compliance
  const targetStrength = parseInt(grade.replace("M", ""));
  let expectedMin = 0;
  if (testAge === 7) {
    expectedMin = targetStrength * 0.67; // 67% at 7 days
  } else if (testAge === 14) {
    expectedMin = targetStrength * 0.83; // 83% at 14 days
  } else {
    expectedMin = targetStrength; // 100% at 28 days
  }
  const compliance = strength >= expectedMin;
  const complianceStatus = compliance ? "PASS" : "FAIL";

  const handleDownloadPDF = () => {
    try {
      downloadStrengthTestPDF({
        reportId,
        batchId,
        testDate,
        testAge,
        strength,
        cubeWeight,
        cubeSize,
        load,
        grade,
        location,
        customerName,
        projectName,
        technicianName,
        testingMachine,
      });
      setToastMessage("PDF downloaded successfully!");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (error) {
      console.error("PDF generation error:", error);
      setToastMessage("Failed to generate PDF");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: `${testAge}-Day Strength Test Report`,
      text: `Batch: ${batchId}\nStrength: ${strength} MPa\nGrade: ${grade}\nStatus: ${complianceStatus}`,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        setToastMessage("Shared successfully!");
      } else {
        // Try clipboard as fallback, but handle if it's blocked
        try {
          await navigator.clipboard.writeText(`${shareData.title}\n\n${shareData.text}`);
          setToastMessage("Report details copied to clipboard");
        } catch (clipboardError) {
          setToastMessage("Share feature not available in this environment");
        }
      }
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (error) {
      console.log("Share cancelled or failed");
    }
  };

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: "spring", duration: 0.5 }}
        className="fixed inset-4 md:inset-8 lg:inset-16 bg-white rounded-3xl z-50 overflow-hidden flex flex-col"
        style={{
          boxShadow: "0 24px 48px rgba(0, 0, 0, 0.2), 0 12px 24px rgba(0, 0, 0, 0.12)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="px-6 py-5 flex items-center justify-between border-b"
          style={{ borderColor: "#E5E7EB" }}
        >
          <div>
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "800",
                color: "#1B2A5B",
                letterSpacing: "-0.02em",
              }}
            >
              {testAge}-Day Cube Strength Test Report
            </h2>
            <p style={{ fontSize: "12px", color: "#717182", marginTop: "2px" }}>
              Report ID: {reportId}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: "#F9FAFB" }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="#717182"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Report Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Compliance Badge */}
            <div className="flex justify-center">
              <div
                className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl"
                style={{
                  backgroundColor: compliance ? "#0F3D2E08" : "#7A3E0008",
                  border: `1.5px solid ${compliance ? "#0F3D2E30" : "#7A3E0030"}`,
                }}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  {compliance ? (
                    <path
                      d="M16.25 5L7.5 13.75L3.75 10"
                      stroke="#0F3D2E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  ) : (
                    <path
                      d="M15 5L5 15M5 5L15 15"
                      stroke="#7A3E00"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  )}
                </svg>
                <span
                  style={{
                    fontSize: "16px",
                    fontWeight: "800",
                    color: compliance ? "#0F3D2E" : "#7A3E00",
                    letterSpacing: "0.02em",
                  }}
                >
                  {complianceStatus}
                </span>
              </div>
            </div>

            {/* Project Information */}
            <div
              className="rounded-2xl p-5"
              style={{ backgroundColor: "#F9FAFB", border: "1px solid #E5E7EB" }}
            >
              <h3
                style={{
                  fontSize: "13px",
                  fontWeight: "800",
                  color: "#1B2A5B",
                  marginBottom: "12px",
                  letterSpacing: "0.01em",
                }}
              >
                PROJECT INFORMATION
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <InfoRow label="Customer Name" value={customerName} />
                <InfoRow label="Project Name" value={projectName} />
                <InfoRow label="Batch ID" value={batchId} />
                <InfoRow label="Grade of Concrete" value={grade} />
                <InfoRow label="Test Date" value={testDate} />
                <InfoRow label="Casting Location" value={location} />
              </div>
            </div>

            {/* Test Results */}
            <div
              className="rounded-2xl p-5"
              style={{ backgroundColor: "#F9FAFB", border: "1px solid #E5E7EB" }}
            >
              <h3
                style={{
                  fontSize: "13px",
                  fontWeight: "800",
                  color: "#1B2A5B",
                  marginBottom: "12px",
                  letterSpacing: "0.01em",
                }}
              >
                TEST RESULTS
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <InfoRow label="Test Age" value={`${testAge} days`} />
                <InfoRow label="Cube Size" value={cubeSize} />
                <InfoRow label="Cube Weight" value={`${cubeWeight} g`} />
                <InfoRow label="Applied Load" value={`${load} N`} />
                <InfoRow label="Cross-sectional Area" value="22500 mm²" />
                <InfoRow 
                  label="Compressive Strength" 
                  value={`${strength} MPa`}
                  highlight
                  highlightColor={compliance ? "#0F3D2E" : "#7A3E00"}
                />
              </div>
              <div className="mt-4 pt-4" style={{ borderTop: "1px solid #E5E7EB" }}>
                <InfoRow 
                  label="Required Minimum Strength" 
                  value={`≥ ${expectedMin.toFixed(1)} MPa`}
                />
                <InfoRow label="Test Standard" value="IS 516:1959" />
              </div>
            </div>

            {/* Test Execution Details */}
            <div
              className="rounded-2xl p-5"
              style={{ backgroundColor: "#F9FAFB", border: "1px solid #E5E7EB" }}
            >
              <h3
                style={{
                  fontSize: "13px",
                  fontWeight: "800",
                  color: "#1B2A5B",
                  marginBottom: "12px",
                  letterSpacing: "0.01em",
                }}
              >
                TEST EXECUTION DETAILS
              </h3>
              <div className="space-y-3">
                <InfoRow label="Testing Machine" value={testingMachine} />
                <InfoRow label="Technician Name" value={technicianName} />
                <InfoRow label="Curing Method" value="Water curing at 27±2°C" />
                <InfoRow label="Compliance Standard" value="IS 456:2000" />
              </div>
            </div>

            {/* Compliance Summary */}
            <div
              className="rounded-2xl p-5"
              style={{
                backgroundColor: compliance ? "#0F3D2E08" : "#7A3E0008",
                border: `1px solid ${compliance ? "#0F3D2E20" : "#7A3E0020"}`,
              }}
            >
              <p style={{ fontSize: "13px", lineHeight: "1.6", color: "#0A0F1C" }}>
                {compliance
                  ? `The concrete sample has achieved ${strength} MPa compressive strength at ${testAge} days, which exceeds the minimum required strength of ${expectedMin.toFixed(1)} MPa for ${grade} grade concrete. The test result is COMPLIANT with IS 456:2000 specifications.`
                  : `The concrete sample has achieved ${strength} MPa compressive strength at ${testAge} days, which is below the minimum required strength of ${expectedMin.toFixed(1)} MPa for ${grade} grade concrete. The test result is NON-COMPLIANT with IS 456:2000 specifications.`}
              </p>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div
          className="px-6 py-4 border-t flex items-center gap-3"
          style={{ borderColor: "#E5E7EB" }}
        >
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={handleShare}
            className="flex-1 py-4 rounded-xl flex items-center justify-center gap-2"
            style={{
              backgroundColor: "#F9FAFB",
              color: "#1B2A5B",
              fontSize: "14px",
              fontWeight: "700",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M13.5 6.75C14.3284 6.75 15 6.07843 15 5.25C15 4.42157 14.3284 3.75 13.5 3.75C12.6716 3.75 12 4.42157 12 5.25C12 6.07843 12.6716 6.75 13.5 6.75Z"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M4.5 11.25C5.32843 11.25 6 10.5784 6 9.75C6 8.92157 5.32843 8.25 4.5 8.25C3.67157 8.25 3 8.92157 3 9.75C3 10.5784 3.67157 11.25 4.5 11.25Z"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M13.5 15.75C14.3284 15.75 15 15.0784 15 14.25C15 13.4216 14.3284 12.75 13.5 12.75C12.6716 12.75 12 13.4216 12 14.25C12 15.0784 12.6716 15.75 13.5 15.75Z"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path d="M5.85 10.65L12.15 13.35M12.15 6.15L5.85 9.35" stroke="currentColor" strokeWidth="1.5" />
            </svg>
            <span>Share</span>
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.98 }}
            onClick={handleDownloadPDF}
            className="flex-1 py-4 rounded-xl flex items-center justify-center gap-2"
            style={{
              background: "linear-gradient(135deg, #1B2A5B 0%, #0F1B3D 100%)",
              color: "white",
              fontSize: "14px",
              fontWeight: "700",
              boxShadow: "0 4px 12px rgba(27, 42, 91, 0.3)",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M15.75 11.25V14.25C15.75 14.6478 15.592 15.0294 15.3107 15.3107C15.0294 15.592 14.6478 15.75 14.25 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0294 2.25 14.6478 2.25 14.25V11.25"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5.25 7.5L9 11.25L12.75 7.5"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M9 11.25V2.25" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Download PDF</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Toast Notification */}
      {showToast && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-[60] px-6 py-4 rounded-2xl"
          style={{
            background: "linear-gradient(135deg, #0F3D2E 0%, #1A5A43 100%)",
            color: "white",
            fontSize: "14px",
            fontWeight: "700",
            boxShadow: "0 8px 24px rgba(15, 61, 46, 0.4)",
          }}
        >
          {toastMessage}
        </motion.div>
      )}
    </>
  );
}

function InfoRow({ 
  label, 
  value, 
  highlight = false,
  highlightColor = "#1B2A5B"
}: { 
  label: string; 
  value: string; 
  highlight?: boolean;
  highlightColor?: string;
}) {
  return (
    <div>
      <p style={{ fontSize: "11px", color: "#717182", marginBottom: "2px" }}>
        {label}
      </p>
      <p
        style={{
          fontSize: highlight ? "16px" : "13px",
          fontWeight: highlight ? "800" : "700",
          color: highlight ? highlightColor : "#0A0F1C",
        }}
      >
        {value}
      </p>
    </div>
  );
}
